import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

/* ── Types ── */
interface Snapshot {
  date: string;
  nav: number;
}

interface SnapshotsResponse {
  data: Snapshot[];
  total: number;
}

/* ── Derived daily P&L ── */
interface DailyPnl {
  dateLabel: string;
  pnl: number;
}

function computeDailyPnl(snapshots: Snapshot[]): DailyPnl[] {
  const sorted = [...snapshots].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const result: DailyPnl[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const pnl = sorted[i].nav - sorted[i - 1].nav;
    result.push({
      dateLabel: new Date(sorted[i].date).toLocaleDateString("en-IN", {
        month: "short",
        day: "2-digit",
      }),
      pnl,
    });
  }
  return result;
}

/* ── Tooltip ── */
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div
      style={{
        background: "#2b2b2b",
        border: "1px solid #444",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 13,
      }}
    >
      <div style={{ color: "#aaa", marginBottom: 4 }}>{label}</div>
      <div style={{ color: val >= 0 ? "#4caf50" : "#ef5350" }}>
        P&L: {val >= 0 ? "+" : ""}
        {formatINR(val)}
      </div>
    </div>
  );
}

/* ── Component ── */
export default function PnlChart() {
  const { data, isLoading, isError } = useQuery<SnapshotsResponse>({
    queryKey: ["portfolio-snapshots"],
    queryFn: () => api.get<SnapshotsResponse>("/portfolio/snapshots"),
    refetchInterval: 60_000,
  });

  const snapshots = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--text-muted)" }}>Loading P&L chart…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--red-light)" }}>Failed to load snapshots</span>
      </div>
    );
  }

  const pnlData = computeDailyPnl(snapshots);

  if (pnlData.length === 0) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--text-muted)" }}>
          No data yet — start trading to see daily P&L
        </span>
      </div>
    );
  }

  /* Aggregate stats */
  const totalPnl = pnlData.reduce((s, d) => s + d.pnl, 0);
  const winDays = pnlData.filter((d) => d.pnl >= 0).length;
  const loseDays = pnlData.length - winDays;

  return (
    <div className="card" style={{ minHeight: 280 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.05 }}>
          Daily P&L
        </h3>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)" }}>
          <span>
            Total:{" "}
            <span style={{ color: totalPnl >= 0 ? "#4caf50" : "#ef5350", fontWeight: 600 }}>
              {totalPnl >= 0 ? "+" : ""}
              {formatINR(totalPnl)}
            </span>
          </span>
          <span>
            {winDays}W / {loseDays}L
          </span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={pnlData} margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fill: "#999", fontSize: 11 }}
            axisLine={{ stroke: "#444" }}
            tickLine={{ stroke: "#444" }}
          />
          <YAxis
            tick={{ fill: "#999", fontSize: 11 }}
            axisLine={{ stroke: "#444" }}
            tickLine={{ stroke: "#444" }}
            tickFormatter={(v: number) => `₹${v >= 0 ? "" : "-"}${Math.abs(v / 1000).toFixed(1)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="pnl" radius={[3, 3, 0, 0]} maxBarSize={32}>
            {pnlData.map((entry, idx) => (
              <Cell
                key={idx}
                fill={entry.pnl >= 0 ? "#4caf50" : "#ef5350"}
                fillOpacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
